import { useState, useEffect, useCallback } from 'react'
import axios from 'axios'
import {
    Box,
    TextField,
    Button,
    Typography,
    Paper,
    InputAdornment,
    IconButton,
} from '@mui/material'
import { Visibility, VisibilityOff } from '@mui/icons-material'
import { logout } from '../ProtectRoute'
import { urlConfig } from '../../config/apiConfig'
import CancelRoundedIcon from '@mui/icons-material/CancelRounded'
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded'
import axiosInstance from '../../config/axiosConfig'

function PasswordResetPage() {
    const [password, setPassword] = useState('')
    const [confirmPassword, setConfirmPassword] = useState('')
    const [showPassword, setShowPassword] = useState(false)
    const [showConfirmPassword, setShowConfirmPassword] = useState(false)
    const [passwordError, setPasswordError] = useState(false)
    const [confirmPasswordError, setConfirmPasswordError] = useState(false)
    const [passwordStrength, setPasswordStrength] = useState(0)
    const [expired, setExpired] = useState(false)
    const [error, setError] = useState(false)
    const [isSuccess, setIsSuccess] = useState(false)
    const [loading, setLoading] = useState(false)
    const [auth, setAuth] = useState('')
    const urlParams = new URLSearchParams(window.location.search)
    const token = urlParams.get('tk')
    const byAdmin = urlParams.get('byAdmin')

    const validateToken = useCallback(async () => {
        if (!token) {
            logout()
            return false
        }

        try {
            const url = byAdmin ? urlConfig.myToken : urlConfig.forgot_validate
            const response = byAdmin
                ? await axiosInstance.get(url, {
                      headers: { Authorization: `Bearer ${token}` },
                  })
                : await axios.post(url, { token })
            if (byAdmin) {
                setAuth(response.data.message)
            }
            return true
        } catch (error) {
            // console.error('Error:', error)
            setExpired(true)
            return false
        }
    }, [byAdmin, token])

    useEffect(() => {
        validateToken()
    }, [validateToken])

    const handlePasswordChange = (e) => {
        const value = e.target.value
        setPassword(value)

        let level = 0

        if (value.length >= 8) level += 1
        if (/[A-Za-z]/.test(value)) level += 1
        if (/\d/.test(value)) level += 1
        if (/[^A-Za-z\d]/.test(value)) level += 1

        setPasswordStrength(level)
        setPasswordError(level < 3 && value.length > 0)
    }

    const getStrengthColor = () => {
        switch (passwordStrength) {
            case 1:
                return '#DA1C23' // Weak (Red)
            case 2:
                return 'orange' // Medium (Orange)
            case 3:
                return '#f0e551' // Very Strong (Yellow)
            case 4:
                return '#15BC11' // Strong (Green)

            default:
                return 'transparent' // Transparent for level 0
        }
    }

    const getStrengthWidth = () => {
        switch (passwordStrength) {
            case 1:
                return '25%' // Weak
            case 2:
                return '50%' // Medium
            case 3:
                return '75%' // Strong
            case 4:
                return '100%' // Very Strong
            default:
                return '0%' // Transparent for level 0
        }
    }

    const handleConfirmPasswordChange = (e) => {
        const value = e.target.value
        setConfirmPassword(value)
        setConfirmPasswordError(value !== password && value.length > 0)
    }

    const handleClickShowPassword = () => {
        setShowPassword(!showPassword)
    }

    const handleClickShowConfirmPassword = () => {
        setShowConfirmPassword(!showConfirmPassword)
    }

    const handleSubmit = async (e) => {
        e.preventDefault()

        if (password.length < 8) {
            setPasswordError(true)
            return
        }

        if (passwordStrength < 3) {
            setPasswordError(true)
            return
        }

        if (!password) {
            setPasswordError(true)
            return
        }

        if (!confirmPassword) {
            setConfirmPasswordError(true)
            return
        }

        if (password !== confirmPassword) {
            setConfirmPasswordError(true)
            return
        }

        if (passwordError) {
            return
        }

        const isValid = await validateToken()
        if (!isValid) return

        setLoading(true)
        try {
            const urlParams = new URLSearchParams(window.location.search)
            const token = urlParams.get('tk')
            if (byAdmin) {
                await axiosInstance.put(
                    `${urlConfig.user}/change-new-password/${auth.user_id} `,
                    {
                        password: password,
                    },
                )
            } else {
                await axios.put(urlConfig.forgot_setPassword, {
                    token,
                    newPassword: password,
                })
            }
            setIsSuccess(true)
        } catch (err) {
            console.error('Error:', err)
            setError(true)
        } finally {
            setLoading(false)
        }
    }

    return (
        <Box
            sx={{
                height: '100vh',
                width: '100vw',
                backgroundColor: '#A81E1A',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden',
            }}
        >
            <Box sx={{ padding: 2 }}>
                <Paper
                    elevation={12}
                    sx={{
                        height: '100%',
                        p: 5,
                        borderRadius: 3,
                        textAlign: 'center',
                    }}
                >
                    <img
                        src='/images/logo_kumwell.png'
                        width={172}
                        alt='Logo'
                    ></img>
                    <Typography
                        variant='subtitle1'
                        sx={{
                            mb: 2,
                            borderBottom: '1px solid black',
                            pb: 1,
                            fontWeight: 500,
                            fontSize: 20,
                        }}
                    >
                        Smart Lightning Management System
                    </Typography>
                    {expired || error || isSuccess ? (
                        <>
                            {isSuccess ? (
                                <CheckCircleRoundedIcon
                                    sx={{
                                        width: '110px',
                                        height: '110px',
                                        color: '#15BC11',
                                    }}
                                />
                            ) : (
                                <CancelRoundedIcon
                                    sx={{
                                        width: '110px',
                                        height: '110px',
                                        color: '#FF0000',
                                    }}
                                />
                            )}
                            {isSuccess ? (
                                <>
                                    <Box sx={{ fontSize: 24 }}>
                                        Password Changed
                                    </Box>
                                    <Box sx={{ fontSize: 18, mt: 1, mb: 5 }}>
                                        Your password has been changed
                                        successfully.
                                    </Box>
                                </>
                            ) : (
                                <>
                                    <Box sx={{ fontSize: 24 }}>
                                        {expired
                                            ? 'Reset code expired'
                                            : ' Failed to reset password'}
                                    </Box>
                                    {expired ? (
                                        <>
                                            <Box sx={{ fontSize: 18, mt: 1 }}>
                                                Password reset code has expired.{' '}
                                            </Box>
                                            <Box sx={{ fontSize: 18, mb: 3 }}>
                                                Please issue a reset request
                                                again.
                                            </Box>
                                        </>
                                    ) : (
                                        <Box
                                            sx={{ fontSize: 18, mb: 5, mt: 1 }}
                                        >
                                            Something went wrong, please try
                                            again later.
                                        </Box>
                                    )}
                                </>
                            )}
                        </>
                    ) : (
                        <>
                            <Typography
                                variant='h6'
                                sx={{
                                    textAlign: 'left',
                                    fontSize: 20,
                                    fontWeight: 500,
                                }}
                            >
                                Reset your password
                            </Typography>
                            <Box
                                component='form'
                                onSubmit={handleSubmit}
                                noValidate
                            >
                                <TextField
                                    sx={{ mt: 2 }}
                                    required
                                    fullWidth
                                    name='password'
                                    label='Enter your new password'
                                    type={showPassword ? 'text' : 'password'}
                                    id='password'
                                    autoComplete='new-password'
                                    value={password}
                                    onChange={handlePasswordChange}
                                    error={passwordError}
                                    helperText={
                                        passwordError
                                            ? password.length < 8
                                                ? 'Password must be at least 8 characters.'
                                                : 'Password strength is too weak.'
                                            : ''
                                    }
                                    InputProps={{
                                        sx: { borderRadius: 2 },
                                        endAdornment: (
                                            <InputAdornment position='end'>
                                                <IconButton
                                                    onClick={
                                                        handleClickShowPassword
                                                    }
                                                    edge='end'
                                                >
                                                    {showPassword ? (
                                                        <VisibilityOff />
                                                    ) : (
                                                        <Visibility />
                                                    )}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                    }}
                                >
                                    <Box
                                        sx={{
                                            height: '8px',
                                            backgroundColor: getStrengthColor(),
                                            width: getStrengthWidth(),
                                            transition:
                                                'width 0.3s ease-in-out',
                                            mt: 1,
                                        }}
                                    />
                                    <Box
                                        sx={{
                                            height: '8px',
                                            backgroundColor: '#F1F1F1',
                                            width: `calc(100% - ${getStrengthWidth()})`,
                                            transition:
                                                'width 0.3s ease-in-out',
                                            mt: 1,
                                        }}
                                    />
                                </Box>
                                <Typography
                                    variant='caption'
                                    color='text.secondary'
                                    sx={{
                                        display: 'block',
                                        textAlign: 'left',
                                        mb: 0,
                                    }}
                                >
                                    Use 8 or more characters with a mix of
                                    letters, numbers & symbols.
                                </Typography>
                                <TextField
                                    margin='normal'
                                    required
                                    fullWidth
                                    name='confirmPassword'
                                    label='Confirm your new password'
                                    type={
                                        showConfirmPassword
                                            ? 'text'
                                            : 'password'
                                    }
                                    id='confirmPassword'
                                    value={confirmPassword}
                                    onChange={handleConfirmPasswordChange}
                                    error={confirmPasswordError}
                                    InputProps={{
                                        sx: { borderRadius: 2 },
                                        endAdornment: (
                                            <InputAdornment position='end'>
                                                <IconButton
                                                    onClick={
                                                        handleClickShowConfirmPassword
                                                    }
                                                    edge='end'
                                                >
                                                    {showConfirmPassword ? (
                                                        <VisibilityOff />
                                                    ) : (
                                                        <Visibility />
                                                    )}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                                {confirmPasswordError && (
                                    <Typography
                                        variant='caption'
                                        color='error'
                                        sx={{
                                            display: 'block',
                                            textAlign: 'left',
                                            mt: 0.5,
                                        }}
                                    >
                                        Passwords do not match.
                                    </Typography>
                                )}
                                <Button
                                    disabled={loading}
                                    type='submit'
                                    fullWidth
                                    variant='contained'
                                    sx={{
                                        borderRadius: 2,
                                        mt: 1,
                                        mb: 2,
                                        backgroundColor: '#e31e24',
                                        '&:hover': {
                                            backgroundColor: '#c22a2a',
                                        },
                                        textTransform: 'none',
                                        py: 1.5,
                                    }}
                                >
                                    Change password
                                </Button>
                            </Box>
                        </>
                    )}{' '}
                    <Box
                        onClick={() => {
                            logout()
                        }}
                        sx={{
                            cursor: 'pointer',
                            color: '#706564',
                            '&:hover': {
                                textDecoration: 'underline',
                            },
                        }}
                    >
                        Back to Sign in
                    </Box>
                </Paper>
            </Box>
        </Box>
    )
}

export default PasswordResetPage
